import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "../../utils/axiosInstance";
import {
  Clock,
  FileXls,
  FilePdf,
  CircleNotch,
  Eraser,
  CheckCircle,
  Trash,
  Lightning,
  ArrowsCounterClockwise,
  Lock,
  LockOpen,
} from "@phosphor-icons/react";

const API = `${window.location.origin}/api`;

const MONTHS_PL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];

const fmt2 = (n) =>
  Number(n || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function computeHours(timeFrom, timeTo) {
  if (!timeFrom || !timeTo) return 0;
  const [hf, mf] = timeFrom.split(":").map(Number);
  const [ht, mt] = timeTo.split(":").map(Number);
  if (Number.isNaN(hf) || Number.isNaN(ht)) return 0;
  let a = hf * 60 + (mf || 0);
  let b = ht * 60 + (mt || 0);
  if (b < a) b += 24 * 60; // overnight
  return Math.round(((b - a) / 60) * 100) / 100;
}

/** Given start time HH:MM and decimal hours, return HH:MM end time (mod 24h). */
function addHoursToTime(timeFrom, hours) {
  if (!timeFrom || !hours || hours <= 0) return "";
  const [hf, mf] = timeFrom.split(":").map(Number);
  if (Number.isNaN(hf)) return "";
  const totalMin = hf * 60 + (mf || 0) + Math.round(hours * 60);
  const m = ((totalMin % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

// Meeus/Jones/Butcher Gregorian Easter algorithm
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

function addDays(year, month, day, delta) {
  const d = new Date(year, month - 1, day);
  d.setDate(d.getDate() + delta);
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

/**
 * Returns object map "MM-DD" → holiday name (Polish official public holidays).
 */
function getPolishHolidays(year) {
  const out = {};
  const fixed = [
    ["01-01", "Nowy Rok"],
    ["01-06", "Trzech Króli"],
    ["05-01", "Święto Pracy"],
    ["05-03", "Święto Konstytucji 3 Maja"],
    ["08-15", "Wniebowzięcie NMP"],
    ["11-01", "Wszystkich Świętych"],
    ["11-11", "Święto Niepodległości"],
    ["12-25", "Boże Narodzenie"],
    ["12-26", "Drugi dzień Świąt"],
  ];
  for (const [k, v] of fixed) out[k] = v;
  const easter = easterSunday(year);
  const map = (label, delta) => {
    const d = addDays(year, easter.month, easter.day, delta);
    out[`${pad2(d.month)}-${pad2(d.day)}`] = label;
  };
  map("Niedziela Wielkanocna", 0);
  map("Poniedziałek Wielkanocny", 1);
  map("Zielone Świątki", 49);
  map("Boże Ciało", 60);
  return out;
}

const DEFAULT_START_KEY = "wh_default_start";
const BLOCK_HOLIDAYS_KEY = "wh_block_holidays";


export default function WorkHoursTab() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [entries, setEntries] = useState([]);  // raw entries for selected emp+month
  const [monthSummary, setMonthSummary] = useState([]);  // per-employee totals
  const [defaultStart, setDefaultStart] = useState(
    () => localStorage.getItem(DEFAULT_START_KEY) || "07:00"
  );
  const [blockHolidays, setBlockHolidays] = useState(
    () => localStorage.getItem(BLOCK_HOLIDAYS_KEY) === "1"
  );
  const [loading, setLoading] = useState(false);
  const [savingDay, setSavingDay] = useState(null);
  const [busy, setBusy] = useState(false);
  const saveTimers = useRef({});

  // Persist default start
  useEffect(() => {
    localStorage.setItem(DEFAULT_START_KEY, defaultStart);
  }, [defaultStart]);

  useEffect(() => {
    localStorage.setItem(BLOCK_HOLIDAYS_KEY, blockHolidays ? "1" : "0");
  }, [blockHolidays]);

  const holidays = useMemo(() => getPolishHolidays(year), [year]);

  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/employees`);
      setEmployees(data || []);
      if (!employeeId && data && data.length) setEmployeeId(data[0]._id);
    } catch (e) {
      console.error(e);
    }
  }, [employeeId]);

  const fetchEntries = useCallback(async (silent = false) => {
    if (!employeeId) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/work-hours`, {
        params: { employee_id: employeeId, year, month },
      });
      setEntries(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [employeeId, year, month]);

  const fetchMonthSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/work-hours/summary-month`, {
        params: { year, month },
      });
      setMonthSummary(data?.rows || []);
    } catch (e) {
      console.error(e);
    }
  }, [year, month]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { fetchMonthSummary(); }, [fetchMonthSummary]);

  // Build day rows for full month
  const dayRows = useMemo(() => {
    const total = daysInMonth(year, month);
    const byDay = {};
    for (const e of entries) {
      try {
        const d = parseInt(e.date.slice(8, 10), 10);
        byDay[d] = e;
      } catch { /* ignore */ }
    }
    const rows = [];
    for (let d = 1; d <= total; d++) {
      const e = byDay[d];
      rows.push({
        day: d,
        date: `${year}-${pad2(month)}-${pad2(d)}`,
        id: e?.id || null,
        time_from: e?.time_from || "",
        time_to: e?.time_to || "",
        hours: e?.hours || 0,
        notes: e?.notes || "",
      });
    }
    return rows;
  }, [entries, year, month]);

  const [localRows, setLocalRows] = useState([]);
  useEffect(() => { setLocalRows(dayRows); }, [dayRows]);

  const totals = useMemo(() => {
    const h = localRows.reduce((s, r) => s + Number(r.hours || 0), 0);
    const dw = localRows.filter((r) => Number(r.hours || 0) > 0).length;
    return { total: Math.round(h * 100) / 100, days: dw };
  }, [localRows]);

  const persistRow = useCallback(async (row) => {
    // Save if there's any meaningful content
    const hasContent = row.time_to || row.time_from || row.notes || Number(row.hours) > 0;
    if (!hasContent && !row.id) return;
    setSavingDay(row.day);
    try {
      // If there's a time range, backend recomputes hours from it.
      // If only manual `hours` is set (no time_to), send hours so backend stores it.
      const payload = {
        employee_id: employeeId,
        date: row.date,
        time_from: row.time_from || null,
        time_to: row.time_to || null,
        notes: row.notes || null,
      };
      if (!row.time_to && Number(row.hours) > 0) {
        payload.hours = Number(row.hours);
      }
      await axios.post(`${API}/work-hours`, payload);
      await fetchEntries(true);
      await fetchMonthSummary();
    } catch (e) {
      alert("Błąd zapisu: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingDay(null);
    }
  }, [employeeId, fetchEntries, fetchMonthSummary]);

  const scheduleSave = useCallback((row) => {
    if (saveTimers.current[row.day]) clearTimeout(saveTimers.current[row.day]);
    saveTimers.current[row.day] = setTimeout(() => persistRow(row), 600);
  }, [persistRow]);

  const updateRow = (day, patch) => {
    setLocalRows((rows) => rows.map((r) => {
      if (r.day !== day) return r;
      const next = { ...r, ...patch };
      // Direct hours entry: if start time exists (or default), compute end time
      if ("hours" in patch) {
        const h = Number(patch.hours) || 0;
        if (h > 0) {
          if (!next.time_from && defaultStart) next.time_from = defaultStart;
          if (next.time_from) {
            next.time_to = addHoursToTime(next.time_from, h);
            next.hours = computeHours(next.time_from, next.time_to);
          } else {
            // No start time available — store manual hours as-is
            next.hours = h;
            next.time_to = "";
          }
        } else {
          next.hours = 0;
        }
      } else if ("time_from" in patch || "time_to" in patch) {
        next.hours = computeHours(next.time_from, next.time_to);
      }
      // Auto-fill default start when user enters time_to and time_from is empty
      if ("time_to" in patch && patch.time_to && !next.time_from && defaultStart) {
        next.time_from = defaultStart;
        next.hours = computeHours(next.time_from, next.time_to);
      }
      // If time_from changed and we have a manual `hours` value but no time_to → recompute time_to
      if ("time_from" in patch && next.time_from && !next.time_to && Number(next.hours) > 0) {
        next.time_to = addHoursToTime(next.time_from, Number(next.hours));
        next.hours = computeHours(next.time_from, next.time_to);
      }
      scheduleSave(next);
      return next;
    }));
  };

  const clearRow = async (row) => {
    if (!row.id) {
      setLocalRows((rows) => rows.map((r) => (r.day === row.day
        ? { ...r, time_from: "", time_to: "", hours: 0, notes: "" } : r)));
      return;
    }
    if (!window.confirm(`Usunąć wpis dla ${row.date}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/work-hours/${row.id}`);
      await fetchEntries(true);
      await fetchMonthSummary();
    } catch (e) {
      alert("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const [fillMinHours, setFillMinHours] = useState(8);
  const [fillMaxHours, setFillMaxHours] = useState(8);
  const [fillTotalHours, setFillTotalHours] = useState("");

  /** Pick a random value in [min, max] rounded to nearest 0.5h. */
  const pickRandomHours = useCallback(() => {
    let lo = Number(fillMinHours);
    let hi = Number(fillMaxHours);
    if (Number.isNaN(lo) || lo <= 0) lo = 8;
    if (Number.isNaN(hi) || hi <= 0) hi = lo;
    if (hi < lo) [lo, hi] = [hi, lo];
    const steps = Math.round((hi - lo) / 0.5);
    const idx = Math.floor(Math.random() * (steps + 1));
    return Math.round((lo + idx * 0.5) * 100) / 100;
  }, [fillMinHours, fillMaxHours]);

  /**
   * Distribute `target` hours across working days (each day getting a value
   * in [lo, hi] in 0.5h steps). Returns array of hour values, one per working
   * day. Length = number of days that should be filled; the remaining working
   * days are left as breaks.
   */
  const distributeTargetHours = useCallback((target, workingDaysCount) => {
    let lo = Number(fillMinHours);
    let hi = Number(fillMaxHours);
    if (Number.isNaN(lo) || lo <= 0) lo = 8;
    if (Number.isNaN(hi) || hi <= 0) hi = lo;
    if (hi < lo) [lo, hi] = [hi, lo];
    if (target <= 0) return [];
    // Round target to 0.5h granularity
    const tgtSteps = Math.round(target / 0.5);
    const loSteps = Math.round(lo / 0.5);
    const hiSteps = Math.round(hi / 0.5);
    if (loSteps <= 0) return [];

    // Number of days to fill: pick value n such that n*lo <= target <= n*hi
    let nMin = Math.ceil(tgtSteps / hiSteps);
    let nMax = Math.floor(tgtSteps / loSteps);
    nMin = Math.max(1, nMin);
    nMax = Math.min(workingDaysCount, nMax);
    if (nMin > nMax) {
      // Target unreachable exactly; clamp to closest feasible n
      if (nMax < 1) nMax = 1;
      nMin = nMax;
    }
    // Prefer a value near target/avg
    const avgSteps = (loSteps + hiSteps) / 2;
    const preferred = Math.round(tgtSteps / avgSteps);
    let n = Math.max(nMin, Math.min(nMax, preferred));

    // Initialise all at lo, then distribute remaining 0.5-steps randomly until done
    const arr = new Array(n).fill(loSteps);
    let remaining = tgtSteps - n * loSteps;
    // If remaining > n*(hi-lo) → impossible; clamp
    const maxAdd = n * (hiSteps - loSteps);
    if (remaining > maxAdd) remaining = maxAdd;
    if (remaining < 0) remaining = 0;
    // Distribute remaining +0.5h chunks at random positions
    const safeguard = remaining + 1000;
    let it = 0;
    while (remaining > 0 && it < safeguard) {
      const i = Math.floor(Math.random() * n);
      if (arr[i] < hiSteps) {
        arr[i] += 1;
        remaining -= 1;
      }
      it += 1;
    }
    // Convert back to hours (×0.5)
    return arr.map((s) => s * 0.5);
  }, [fillMinHours, fillMaxHours]);

  /** Shuffle helper (Fisher-Yates) */
  const shuffle = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  /** Build payload of entries to set for one employee for the current month.
   *  - If `fillTotalHours` > 0: random subset of working days totalling target.
   *  - Else: every working day gets a random value in [min, max].
   *  Returns array of {date, time_from, time_to} for all 1..N days (empty ones
   *  marked with empty strings → represent gaps / breaks).
   */
  const buildMonthEntries = useCallback(() => {
    const workingDays = [];
    for (const row of localRows) {
      const dow = new Date(year, month - 1, row.day).getDay();
      const isSunday = dow === 0;
      const mmdd = row.date.slice(5);
      const isHoliday = !!holidays[mmdd];
      if (isSunday || isHoliday) continue;
      workingDays.push(row);
    }

    const total = Number(fillTotalHours) || 0;
    let entries = [];

    if (total > 0) {
      // Target mode: pick n days, distribute hours
      const hoursDist = distributeTargetHours(total, workingDays.length);
      const n = hoursDist.length;
      const picked = shuffle(workingDays).slice(0, n)
        .sort((a, b) => a.day - b.day);
      const hoursByDay = {};
      picked.forEach((row, idx) => { hoursByDay[row.day] = hoursDist[idx]; });
      for (const row of workingDays) {
        const h = hoursByDay[row.day];
        if (h && h > 0) {
          const tf = defaultStart;
          const tt = addHoursToTime(tf, h);
          entries.push({ date: row.date, time_from: tf, time_to: tt });
        }
      }
    } else {
      // Per-day random in [min, max]
      for (const row of workingDays) {
        const h = pickRandomHours();
        const tf = defaultStart;
        const tt = addHoursToTime(tf, h);
        entries.push({ date: row.date, time_from: tf, time_to: tt });
      }
    }
    return entries;
  }, [localRows, year, month, holidays, defaultStart, fillTotalHours, distributeTargetHours, pickRandomHours]);

  const fillEntireMonth = async () => {
    if (!employeeId) return;
    if (!defaultStart) { alert("Najpierw ustaw domyślną godzinę rozpoczęcia."); return; }
    const lo = Math.min(Number(fillMinHours), Number(fillMaxHours));
    const hi = Math.max(Number(fillMinHours), Number(fillMaxHours));
    const total = Number(fillTotalHours) || 0;
    const targetTxt = total > 0
      ? `razem ${total} godz./mies. (po ${lo === hi ? lo : `${lo}–${hi}`} godz./dzień; reszta dni — przerwy)`
      : `${lo === hi ? `${lo} godz.` : `${lo}–${hi} godz. (losowo)`}/dzień`;
    if (!window.confirm(
      `Wypełnić cały miesiąc — ${targetTxt}? Pominięte zostaną niedziele i polskie święta. Istniejące wpisy zostaną zastąpione.`
    )) return;
    setBusy(true);
    try {
      const entries = buildMonthEntries();
      const { data } = await axios.post(`${API}/work-hours/bulk-replace`, {
        employee_id: employeeId,
        year, month,
        entries,
      });
      await fetchEntries(true);
      await fetchMonthSummary();
      const sum = entries.reduce((s, e) => {
        const [a, b] = [e.time_from, e.time_to].map((t) => {
          const [hh, mm] = t.split(":").map(Number);
          return hh * 60 + mm;
        });
        let d = b - a; if (d < 0) d += 24 * 60;
        return s + d / 60;
      }, 0);
      alert(`Zapisano: ${data.inserted} dni · razem ${sum.toFixed(2)} godz. (usunięto poprzednich: ${data.deleted}).`);
    } catch (e) {
      alert("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const fillEntireMonthAllEmployees = async () => {
    if (!defaultStart) { alert("Najpierw ustaw domyślną godzinę rozpoczęcia."); return; }
    if (employees.length === 0) { alert("Brak pracowników."); return; }
    const lo = Math.min(Number(fillMinHours), Number(fillMaxHours));
    const hi = Math.max(Number(fillMinHours), Number(fillMaxHours));
    const total = Number(fillTotalHours) || 0;
    const targetTxt = total > 0
      ? `razem ${total} godz./mies. (po ${lo === hi ? lo : `${lo}–${hi}`} godz./dzień; reszta dni — przerwy)`
      : `${lo === hi ? `${lo} godz.` : `${lo}–${hi} godz. (losowo)`}/dzień`;
    if (!window.confirm(
      `Wypełnić miesiąc dla WSZYSTKICH ${employees.length} pracowników — ${targetTxt}? Pominięte zostaną niedziele i święta. Istniejące wpisy zostaną zastąpione.`
    )) return;
    setBusy(true);
    try {
      let totalInserted = 0;
      let totalDeleted = 0;
      // Sequential to avoid overloading; each employee gets independent random distribution
      for (const emp of employees) {
        const entries = buildMonthEntries();
        const { data } = await axios.post(`${API}/work-hours/bulk-replace`, {
          employee_id: emp._id,
          year, month,
          entries,
        });
        totalInserted += data.inserted;
        totalDeleted += data.deleted;
      }
      await fetchEntries(true);
      await fetchMonthSummary();
      alert(`Zapisano dla ${employees.length} pracowników · wpisów: ${totalInserted} (usunięto poprzednich: ${totalDeleted}).`);
    } catch (e) {
      alert("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const applyDefaultStartToAll = async () => {
    if (!employeeId) return;
    if (!defaultStart) { alert("Ustaw najpierw domyślną godzinę rozpoczęcia."); return; }
    if (!window.confirm(`Zastosować ${defaultStart} jako godzinę rozpoczęcia we wszystkich dniach miesiąca, w których jest wpisana godzina zakończenia?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/work-hours/bulk-defaults`, {
        employee_id: employeeId,
        year, month,
        default_time_from: defaultStart,
        only_with_time_to: true,
      });
      await fetchEntries(true);
      await fetchMonthSummary();
      alert(`Zaktualizowano dni: ${data.updated}`);
    } catch (e) {
      alert("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const clearDefaultFromAll = async () => {
    if (!employeeId) return;
    if (!window.confirm("Wyczyścić godzinę rozpoczęcia we wszystkich dniach miesiąca?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/work-hours/bulk-defaults`, {
        employee_id: employeeId,
        year, month,
        default_time_from: null,
        only_with_time_to: false,
      });
      await fetchEntries(true);
      await fetchMonthSummary();
    } catch (e) {
      alert("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const downloadFile = async (path, params, filename) => {
    setBusy(true);
    try {
      const res = await axios.get(path, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Błąd pobierania: " + (e.message || ""));
    } finally {
      setBusy(false);
    }
  };

  const exportXlsxAll = () => downloadFile(
    `${API}/work-hours/export/xlsx`,
    { year, month },
    `ewidencja_godzin_${MONTHS_PL[month - 1]}_${year}.xlsx`,
  );
  const exportXlsxOne = () => {
    if (!employeeId) return;
    const emp = employees.find((e) => e._id === employeeId);
    const name = (emp?.name || "pracownik").replace(/\s+/g, "_");
    downloadFile(
      `${API}/work-hours/export/xlsx`,
      { year, month, employee_id: employeeId },
      `ewidencja_${name}_${MONTHS_PL[month - 1]}_${year}.xlsx`,
    );
  };
  const exportPdfOne = () => {
    if (!employeeId) return;
    const emp = employees.find((e) => e._id === employeeId);
    const name = (emp?.name || "pracownik").replace(/\s+/g, "_");
    downloadFile(
      `${API}/work-hours/export/pdf`,
      { employee_id: employeeId, year, month },
      `ewidencja_${name}_${MONTHS_PL[month - 1]}_${year}.pdf`,
    );
  };

  const monthOptions = MONTHS_PL.map((m, i) => ({ value: i + 1, label: m }));
  const yearOptions = [];
  for (let y = today.getFullYear() - 2; y <= today.getFullYear() + 1; y++) yearOptions.push(y);

  const dayNames = ["Pn", "Wt", "Śr", "Cz", "Pt", "So", "Nd"];

  return (
    <div className="space-y-6" data-testid="admin-work-hours-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Clock className="w-6 h-6 text-purple-600" weight="duotone" />
          Ewidencja godzin
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setBlockHolidays((v) => !v)}
            className={`px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 border transition-colors ${
              blockHolidays
                ? "bg-amber-100 hover:bg-amber-200 text-amber-900 border-amber-300"
                : "bg-white hover:bg-slate-50 text-slate-700 border-slate-300"
            }`}
            data-testid="wh-block-holidays-btn"
            title="Zablokuj edycję niedziel i polskich świąt państwowych"
          >
            {blockHolidays ? <Lock className="w-4 h-4" weight="fill" /> : <LockOpen className="w-4 h-4" />}
            {blockHolidays ? "Niedziele/święta zablokowane" : "Zablokuj niedziele/święta"}
          </button>
          <button
            onClick={exportXlsxOne}
            disabled={busy || !employeeId}
            className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            data-testid="wh-export-xlsx-one-btn"
            title="Eksportuj tylko bieżącego pracownika"
          >
            <FileXls className="w-4 h-4" /> XLSX (pracownik)
          </button>
          <button
            onClick={exportXlsxAll}
            disabled={busy}
            className="px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            data-testid="wh-export-xlsx-all-btn"
            title="Eksportuj wszystkich pracowników (po jednej karcie) + podsumowanie"
          >
            <FileXls className="w-4 h-4" /> XLSX (wszyscy)
          </button>
          <button
            onClick={exportPdfOne}
            disabled={busy || !employeeId}
            className="px-3 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            data-testid="wh-export-pdf-btn"
          >
            <FilePdf className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {/* Filters / settings */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Pracownik
            </label>
            <select
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              className="input-field w-full"
              data-testid="wh-employee-select"
            >
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>{emp.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Miesiąc
            </label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              className="input-field w-full"
              data-testid="wh-month-select"
            >
              {monthOptions.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Rok
            </label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="input-field w-full"
              data-testid="wh-year-select"
            >
              {yearOptions.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Domyślna godz. rozpoczęcia
            </label>
            <div className="flex gap-2">
              <input
                type="time"
                value={defaultStart}
                onChange={(e) => setDefaultStart(e.target.value)}
                className="input-field flex-1"
                data-testid="wh-default-start"
              />
              <button
                onClick={applyDefaultStartToAll}
                disabled={busy}
                className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 whitespace-nowrap"
                data-testid="wh-apply-default-btn"
                title="Wstaw godzinę rozpoczęcia we wszystkie dni, w których jest godzina zakończenia"
              >
                <Lightning className="w-4 h-4" weight="fill" /> Zastosuj
              </button>
            </div>
            <button
              onClick={clearDefaultFromAll}
              disabled={busy}
              className="mt-2 text-xs text-slate-500 hover:text-rose-600 flex items-center gap-1"
              data-testid="wh-clear-default-btn"
            >
              <Eraser className="w-3.5 h-3.5" /> Wyczyść godziny rozpoczęcia w miesiącu
            </button>
          </div>
        </div>

        {/* Fill entire month — random hours in range, optional total target */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
            Wypełnij cały miesiąc — losowe godziny w zakresie
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Od (godz./dzień)</label>
              <input
                type="number"
                min="1"
                max="24"
                step="0.5"
                value={fillMinHours}
                onChange={(e) => setFillMinHours(e.target.value)}
                className="input-field w-28"
                data-testid="wh-fill-min-input"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Do (godz./dzień)</label>
              <input
                type="number"
                min="1"
                max="24"
                step="0.5"
                value={fillMaxHours}
                onChange={(e) => setFillMaxHours(e.target.value)}
                className="input-field w-28"
                data-testid="wh-fill-max-input"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Razem h/miesiąc (opcjonalnie)</label>
              <input
                type="number"
                min="0"
                max="744"
                step="0.5"
                value={fillTotalHours}
                onChange={(e) => setFillTotalHours(e.target.value)}
                placeholder="np. 40"
                className="input-field w-32"
                data-testid="wh-fill-total-input"
                title="Jeśli podasz np. 40 — system rozpisze ok. 8 dni × 5 godz., resztę zostawi jako przerwy"
              />
            </div>
            <button
              onClick={fillEntireMonth}
              disabled={busy || !employeeId}
              className="px-4 py-2 bg-foreground hover:bg-black disabled:opacity-50 text-white rounded-lg text-sm font-semibold flex items-center gap-2"
              data-testid="wh-fill-month-btn"
              title="Wypełnij miesiąc tylko dla wybranego pracownika"
            >
              <Lightning className="w-4 h-4" weight="fill" /> Wypełnij (1 pracownik)
            </button>
            <button
              onClick={fillEntireMonthAllEmployees}
              disabled={busy}
              className="px-4 py-2 bg-purple-700 hover:bg-purple-800 disabled:opacity-50 text-white rounded-lg text-sm font-semibold flex items-center gap-2"
              data-testid="wh-fill-month-all-btn"
              title={`Wypełnij miesiąc dla wszystkich ${employees.length} pracowników`}
            >
              <Lightning className="w-4 h-4" weight="fill" /> Wypełnij dla wszystkich ({employees.length})
            </button>
          </div>
          <div className="text-xs text-slate-500 mt-2">
            {Number(fillTotalHours) > 0 ? (
              <>
                <strong className="text-foreground">Tryb celu:</strong> rozdziel {fillTotalHours} godz. po {Math.min(Number(fillMinHours), Number(fillMaxHours)) || 0}–{Math.max(Number(fillMinHours), Number(fillMaxHours)) || 0} h/dzień
                {" "}(~{Math.round(Number(fillTotalHours) / (((Number(fillMinHours) || 0) + (Number(fillMaxHours) || 0)) / 2 || 1))} dni roboczych) ·
                pozostałe dni będą przerwami · zastąpi istniejące wpisy.
              </>
            ) : (
              <>
                Losowe wartości w zakresie {Math.min(Number(fillMinHours), Number(fillMaxHours)) || 0}–{Math.max(Number(fillMinHours), Number(fillMaxHours)) || 0} godz. dziennie ·
                każdy dzień roboczy · pomija niedziele i polskie święta · zastąpi istniejące wpisy.
              </>
            )}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Razem godzin</div>
          <div className="text-2xl font-bold text-purple-700 mt-1 tabular-nums" data-testid="wh-total-hours">
            {fmt2(totals.total)} h
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Przepracowane dni</div>
          <div className="text-2xl font-bold text-slate-800 mt-1 tabular-nums" data-testid="wh-days-worked">{totals.days}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Miesiąc / Rok</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">{MONTHS_PL[month - 1]} {year}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pracowników z wpisami</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1 tabular-nums">{monthSummary.length}</div>
        </div>
      </div>

      {/* Day-by-day grid */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-600" weight="duotone" />
            Karta godzin — {MONTHS_PL[month - 1]} {year}
          </h3>
          <button
            onClick={() => { fetchEntries(); fetchMonthSummary(); }}
            className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md flex items-center gap-1.5 text-slate-700"
            data-testid="wh-refresh-btn"
          >
            <ArrowsCounterClockwise className="w-3.5 h-3.5" /> Odśwież
          </button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-400">Ładowanie...</div>
        ) : !employeeId ? (
          <div className="py-12 text-center text-slate-500">Wybierz pracownika.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-2 px-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-28">Data</th>
                  <th className="text-left py-2 px-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-12">Dzień</th>
                  <th className="text-left py-2 px-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-32">Od</th>
                  <th className="text-left py-2 px-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-32">Do</th>
                  <th className="text-right py-2 px-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-24">Godziny</th>
                  <th className="text-left py-2 px-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Uwagi</th>
                  <th className="text-center py-2 px-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-20">Status</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {localRows.map((row) => {
                  const dow = new Date(year, month - 1, row.day).getDay(); // 0..6 Sun..Sat
                  const dowIdx = (dow + 6) % 7; // 0=Mon..6=Sun
                  const isSunday = dowIdx === 6;
                  const isSaturday = dowIdx === 5;
                  const isWeekend = dowIdx >= 5;
                  const mmdd = row.date.slice(5);
                  const holidayName = holidays[mmdd] || null;
                  const isBlocked = blockHolidays && (isSunday || !!holidayName);
                  const blockReason = isSunday && holidayName
                    ? `Niedziela — ${holidayName}`
                    : isSunday
                      ? "Niedziela"
                      : holidayName || "";
                  return (
                    <tr
                      key={row.day}
                      className={`border-t border-slate-100 ${
                        isBlocked
                          ? "bg-amber-50/70"
                          : isWeekend
                            ? "bg-slate-50/60"
                            : ""
                      } ${isBlocked ? "" : "hover:bg-purple-50/30"}`}
                      data-testid={`wh-row-${row.day}`}
                    >
                      <td className="py-1.5 px-3 font-mono text-slate-700">
                        <div className="flex items-center gap-2">
                          <span>{row.date}</span>
                          {isBlocked && (
                            <Lock className="w-3.5 h-3.5 text-amber-600" weight="fill" />
                          )}
                        </div>
                        {holidayName && (
                          <div
                            className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mt-0.5"
                            data-testid={`wh-holiday-${row.day}`}
                          >
                            {holidayName}
                          </div>
                        )}
                      </td>
                      <td className={`py-1.5 px-3 text-xs ${isSaturday || isSunday ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                        {dayNames[dowIdx]}
                      </td>
                      <td className="py-1.5 px-3">
                        <input
                          type="time"
                          value={row.time_from}
                          onChange={(e) => updateRow(row.day, { time_from: e.target.value })}
                          disabled={isBlocked}
                          className="border border-slate-200 rounded px-2 py-1 w-full bg-white disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                          data-testid={`wh-from-${row.day}`}
                          title={isBlocked ? blockReason : undefined}
                        />
                      </td>
                      <td className="py-1.5 px-3">
                        <input
                          type="time"
                          value={row.time_to}
                          onChange={(e) => updateRow(row.day, { time_to: e.target.value })}
                          disabled={isBlocked}
                          className="border border-slate-200 rounded px-2 py-1 w-full bg-white disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                          data-testid={`wh-to-${row.day}`}
                          title={isBlocked ? blockReason : undefined}
                        />
                      </td>
                      <td className="py-1.5 px-3">
                        <input
                          type="number"
                          step="0.25"
                          min="0"
                          max="24"
                          value={row.hours || ""}
                          onChange={(e) => updateRow(row.day, { hours: e.target.value })}
                          disabled={isBlocked}
                          placeholder="—"
                          className="border border-slate-200 rounded px-2 py-1 w-full bg-white text-right tabular-nums font-semibold text-slate-800 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                          data-testid={`wh-hours-${row.day}`}
                          title={isBlocked ? blockReason : "Wpisz godziny — czas zakończenia obliczy się automatycznie"}
                        />
                      </td>
                      <td className="py-1.5 px-3">
                        <input
                          type="text"
                          value={row.notes}
                          onChange={(e) => updateRow(row.day, { notes: e.target.value })}
                          disabled={isBlocked}
                          placeholder={isBlocked ? blockReason : "—"}
                          className="border border-slate-200 rounded px-2 py-1 w-full bg-white disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed disabled:italic"
                          data-testid={`wh-notes-${row.day}`}
                          title={isBlocked ? blockReason : undefined}
                        />
                      </td>
                      <td className="py-1.5 px-3 text-center">
                        {savingDay === row.day ? (
                          <CircleNotch className="w-4 h-4 animate-spin text-purple-600 inline-block" />
                        ) : row.id ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600 inline-block" weight="fill" />
                        ) : (
                          <span className="text-slate-300 text-xl">○</span>
                        )}
                      </td>
                      <td className="py-1.5 px-3 text-center">
                        {row.id && (
                          <button
                            onClick={() => clearRow(row)}
                            className="p-1 hover:bg-rose-100 rounded text-rose-600"
                            title="Usuń wpis"
                            data-testid={`wh-delete-${row.day}`}
                          >
                            <Trash className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-purple-50 border-t-2 border-purple-200">
                  <td colSpan={4} className="py-2.5 px-3 text-right font-bold text-slate-700">Razem godzin:</td>
                  <td className="py-2.5 px-3 text-right font-bold text-purple-700 tabular-nums">{fmt2(totals.total)}</td>
                  <td colSpan={3} className="py-2.5 px-3 text-sm text-slate-500">
                    Dni przepracowanych: <span className="font-bold">{totals.days}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Month summary across all employees */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">
            Zbiorcze podsumowanie — {MONTHS_PL[month - 1]} {year}
          </h3>
        </div>
        {monthSummary.length === 0 ? (
          <div className="py-8 text-center text-slate-500">Brak wpisów w tym miesiącu.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-2 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Pracownik</th>
                  <th className="text-right py-2 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Suma godzin</th>
                  <th className="text-right py-2 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">Dni przepracowanych</th>
                </tr>
              </thead>
              <tbody>
                {monthSummary.map((r) => (
                  <tr
                    key={r.employee_id}
                    className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${r.employee_id === employeeId ? "bg-purple-50" : ""}`}
                    onClick={() => setEmployeeId(r.employee_id)}
                    data-testid={`wh-summary-row-${r.employee_id}`}
                  >
                    <td className="py-2 px-4 font-semibold text-slate-800">{r.employee_name || r.employee_id}</td>
                    <td className="py-2 px-4 text-right tabular-nums font-bold text-purple-700">{fmt2(r.total_hours)} h</td>
                    <td className="py-2 px-4 text-right tabular-nums text-slate-700">{r.days_worked}</td>
                  </tr>
                ))}
                <tr className="bg-purple-50 border-t-2 border-purple-200 font-bold">
                  <td className="py-2.5 px-4 text-slate-800">Razem</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-purple-700">
                    {fmt2(monthSummary.reduce((s, r) => s + Number(r.total_hours || 0), 0))} h
                  </td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-700">
                    {monthSummary.reduce((s, r) => s + Number(r.days_worked || 0), 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
